import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowRight, Wrench, Sun, Zap, Calendar, Sparkles, AlertTriangle } from "lucide-react";

const SERVICE_TYPES = [
  {
    id: "consultation",
    label: "Solar Consultation",
    icon: Sun,
    desc: "30-min on-site review of your roof, bill and energy goals.",
    price: "Free",
    accent: "amber",
    eta: "Within 3 business days",
  },
  {
    id: "solar_install",
    label: "Solar Installation",
    icon: Sun,
    desc: "Rooftop panels, hybrid inverters and battery storage — end-to-end.",
    price: "Custom quote",
    accent: "gold",
    eta: "Quote within 48h",
  },
  {
    id: "solar_upgrade",
    label: "Solar Upgrade",
    icon: Sparkles,
    desc: "Add panels, battery storage or smart monitoring to an existing system.",
    price: "from $1,500",
    accent: "gold",
    eta: "Quote within 48h",
  },
  {
    id: "electrical",
    label: "Electrical Work",
    icon: Zap,
    desc: "Panels, sub-panels, circuits, outlets, EV chargers — licensed on-island.",
    price: "from $199",
    accent: "navy",
    eta: "Site visit booked",
  },
  {
    id: "maintenance",
    label: "System Maintenance",
    icon: Wrench,
    desc: "Annual cleaning, performance check, inverter diagnostics and safety report.",
    price: "from $199",
    accent: "slate",
    eta: "Scheduled within 7 days",
  },
];

const TYPE_ACCENTS = {
  amber: { iconBg: "bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700", pill: "bg-amber-50 text-amber-700 border-amber-200", bar: "bg-amber-400" },
  slate: { iconBg: "bg-gradient-to-br from-slate-200 to-slate-300 text-slate-700", pill: "bg-slate-50 text-slate-700 border-slate-200", bar: "bg-slate-400" },
  gold:  { iconBg: "bg-gradient-to-br from-[#F3E0A2] to-[#E0B847] text-[#3D2C00]", pill: "bg-[#FFF7E0] text-[#8A6B14] border-[#E8CF7C]", bar: "bg-[#C9A356]" },
  navy:  { iconBg: "bg-gradient-to-br from-[#1E3A66] to-[#0A2540] text-white", pill: "bg-[#E8EEF8] text-[#0A2540] border-[#C7D4E8]", bar: "bg-[#0A2540]" },
};

const ENERGY_GOALS = [
  "Reduce Electricity Bills",
  "Increase Property Value",
  "Eco-Friendly Living",
  "Backup Power",
  "Off-Grid Independence",
];

export default function ServiceRequest() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [type, setType] = useState(null);
  const [propertyType, setPropertyType] = useState("Single Family Home");
  const [roofType, setRoofType] = useState("Asphalt Shingle, South Facing");
  const [goals, setGoals] = useState([]);
  const [battery, setBattery] = useState("Not Sure");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState(null);
  const [slots, setSlots] = useState([]);
  const [chosenSlot, setChosenSlot] = useState(null);
  const [bookingSlot, setBookingSlot] = useState(false);

  // Pre-fill from landing-page calculator if user just signed up after using it
  useEffect(() => {
    try {
      const raw = localStorage.getItem("pb_calc_intent");
      if (!raw) return;
      const data = JSON.parse(raw);
      // Only honor recent entries (24h)
      if (Date.now() - data.ts > 86400 * 1000) {
        localStorage.removeItem("pb_calc_intent");
        return;
      }
      setType("consultation");
      const roofMap = {
        asphalt: "Asphalt Shingle, South Facing",
        concrete: "Concrete Tile",
        metal: "Metal Roof",
        flat: "Flat Roof",
      };
      const propMap = {
        single_family: "Single Family Home",
        multi_family: "Multi-Family",
        condo: "Apartment / Condo",
        commercial: "Commercial",
      };
      if (data.roof && roofMap[data.roof]) setRoofType(roofMap[data.roof]);
      if (data.property && propMap[data.property]) setPropertyType(propMap[data.property]);
      const utmParts = data.utm
        ? Object.entries(data.utm)
            .filter(([k]) => k.startsWith("utm_"))
            .map(([k, v]) => `${k}=${v}`)
            .join(", ")
        : "";
      const noteLines = [
        `From savings calculator — monthly bill ~$${data.bill}, suggested fit: ${data.recommended}.`,
      ];
      if (data.annual_savings) {
        noteLines.push(`Internal estimate (not shown to client): ~$${data.annual_savings.toLocaleString()}/yr savings, ~$${(data.lifetime_savings || 0).toLocaleString()} over 25 yrs, ~${data.co2_offset_tons || 0}t CO₂/yr offset.`);
      }
      if (utmParts) noteLines.push(`[ad attribution: ${utmParts}]`);
      setNotes(noteLines.join("\n"));
      setStep(2);
      toast.success("We pre-filled your form from the savings calculator", { duration: 4000 });
      localStorage.removeItem("pb_calc_intent");
    } catch (err) {
      console.warn("Calculator pre-fill skipped:", err);
    }
    // Read-once-on-mount of localStorage; intentionally no deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleGoal = (g) => {
    setGoals((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
  };

  // Profile-completeness gate — PowerBuilt needs a phone AND service address
  // before we hand this to dispatch. If either is missing, block the flow with
  // a friendly banner instead of allowing a "silent" incomplete request.
  const profileComplete = !!(user?.phone && user?.phone.trim() && user?.address && user?.address.trim());

  const submit = async () => {
    if (!profileComplete) {
      toast.error("Please add your phone and address first");
      navigate("/dashboard/profile?next=/dashboard/services");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/requests", {
        request_type: type,
        property_type: propertyType,
        roof_type: roofType,
        energy_goals: goals,
        battery_interest: battery,
        notes,
      });
      toast.success("Request submitted! Now pick an appointment time.");
      setRequestId(r.data.request_id);
      // Fetch slots and advance to step 4 (picker)
      const s = await api.get("/appointments/slots");
      setSlots(s.data);
      setStep(4);
    } catch {
      toast.error("Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const bookSlot = async () => {
    if (!chosenSlot) {
      toast.error("Please pick a date and time before confirming");
      return;
    }
    if (!requestId) return;
    setBookingSlot(true);
    try {
      await api.post(`/requests/${requestId}/book-slot`, { appointment_at: chosenSlot });
      toast.success("Consultation booked!");
      setStep(5);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not book slot");
      // Refresh slots
      const s = await api.get("/appointments/slots");
      setSlots(s.data);
    } finally {
      setBookingSlot(false);
    }
  };

  const skipBooking = () => {
    toast.success("No problem — our team will reach out to schedule.");
    setStep(5);
  };

  return (
    <div className="max-w-3xl" data-testid="service-request-page">
      <p className="overline">Request a service</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] mt-2 mb-2">Tell us what you need</h1>
      <p className="text-[#475569] mb-8">Step {Math.min(step, 4)}/4</p>

      <div className="w-full bg-slate-100 rounded-full h-1 mb-10">
        <div className="bg-[#C9A356] h-1 rounded-full transition-all" style={{ width: `${(Math.min(step, 4) / 4) * 100}%` }}></div>
      </div>

      {!profileComplete && step <= 3 && (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40 px-4 py-3" data-testid="profile-required-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-amber-900 dark:text-amber-100">
            <div className="font-semibold">Add your phone and address first</div>
            <p className="text-xs mt-0.5">
              We need both so our team can call to confirm and know where to visit. This takes 30 seconds.
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard/profile?next=/dashboard/services")}
            data-testid="banner-open-profile"
            className="shrink-0 px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold"
          >
            Complete profile
          </button>
        </div>
      )}

      {step === 1 && (
        <div className="card-pb p-8" data-testid="step-1">
          <h2 className="font-heading text-xl text-[#0A2540] mb-1">What service do you need?</h2>
          <p className="text-sm text-[#475569] mb-6">Pick a category — you can add details on the next step.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICE_TYPES.map((s) => {
              const a = TYPE_ACCENTS[s.accent] || TYPE_ACCENTS.gold;
              return (
                <button
                  key={s.id}
                  onClick={() => { setType(s.id); setStep(2); }}
                  data-testid={`service-type-${s.id}`}
                  className="group relative text-left rounded-xl border border-slate-200 hover:border-[#C9A356] hover:shadow-lg bg-white overflow-hidden transition-all hover:-translate-y-0.5"
                >
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className={`w-14 h-14 rounded-2xl ${a.iconBg} grid place-items-center shadow-sm transition-transform group-hover:scale-105`}>
                        <s.icon className="w-7 h-7" strokeWidth={1.5} />
                      </div>
                      <span className={`px-2.5 py-1 rounded-full border text-[11px] font-semibold ${a.pill}`}>{s.price}</span>
                    </div>
                    <div className="font-heading text-lg text-[#0A2540] font-medium">{s.label}</div>
                    <p className="text-xs text-[#475569] mt-1 leading-relaxed min-h-[48px]">{s.desc}</p>
                    <div className="flex items-center gap-1 text-[11px] text-[#94A3B8] mt-3">
                      <Calendar className="w-3 h-3" /> {s.eta}
                    </div>
                  </div>
                  <div className={`h-1 w-full ${a.bar} opacity-70 group-hover:opacity-100 transition-opacity`} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="card-pb p-8 space-y-5" data-testid="step-2">
          <h2 className="font-heading text-xl text-[#0A2540]">Property details</h2>
          <div>
            <label className="text-sm font-medium text-[#0A2540] mb-2 block">Property Type</label>
            <select
              value={propertyType}
              onChange={(e) => setPropertyType(e.target.value)}
              data-testid="property-type-select"
              className="w-full border border-[#E2E8F0] rounded-md px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356]"
            >
              <option>Single Family Home</option>
              <option>Multi-Family</option>
              <option>Apartment / Condo</option>
              <option>Commercial</option>
              <option>Other</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0A2540] mb-2 block">Roof Type / Orientation</label>
            <select
              value={roofType}
              onChange={(e) => setRoofType(e.target.value)}
              data-testid="roof-type-select"
              className="w-full border border-[#E2E8F0] rounded-md px-4 py-2.5 bg-white outline-none focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356]"
            >
              <option>Asphalt Shingle, South Facing</option>
              <option>Concrete Tile</option>
              <option>Metal Roof</option>
              <option>Flat Roof</option>
              <option>Not Sure</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0A2540] mb-2 block">Energy Goals</label>
            <div className="space-y-2">
              {ENERGY_GOALS.map((g) => (
                <label key={g} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={goals.includes(g)}
                    onChange={() => toggleGoal(g)}
                    data-testid={`goal-${g.toLowerCase().replace(/\s+/g, "-")}`}
                    className="rounded border-[#E2E8F0] text-[#C9A356] focus:ring-[#C9A356]"
                  />
                  <span className="text-sm text-[#475569]">{g}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0A2540] mb-2 block">Battery Interest <span className="text-[#475569] font-normal">(Optional)</span></label>
            <div className="flex gap-4">
              {["Yes", "No", "Not Sure"].map((b) => (
                <label key={b} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="battery"
                    value={b}
                    checked={battery === b}
                    onChange={(e) => setBattery(e.target.value)}
                    data-testid={`battery-${b.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-[#C9A356] focus:ring-[#C9A356]"
                  />
                  <span className="text-sm text-[#475569]">{b}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="btn-outline-navy" data-testid="step2-back">Back</button>
            <button onClick={() => setStep(3)} className="btn-accent" data-testid="step2-next">Continue <ArrowRight className="w-4 h-4 ml-2" /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="card-pb p-8 space-y-5" data-testid="step-3">
          <h2 className="font-heading text-xl text-[#0A2540]">Anything else we should know?</h2>
          <textarea
            rows="6"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes, preferred contact times, specific questions..."
            data-testid="notes-textarea"
            className="w-full border border-[#E2E8F0] rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-[#C9A356]/50 focus:border-[#C9A356]"
          />
          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="btn-outline-navy" data-testid="step3-back">Back</button>
            <button onClick={submit} disabled={submitting} className="btn-accent" data-testid="submit-request">
              {submitting ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="card-pb p-8" data-testid="step-4-slots">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-md bg-[#F3E8CE] grid place-items-center text-[#C9A356] shrink-0">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-heading text-xl text-[#0A2540]">Pick a time for your consultation</h2>
              <p className="text-sm text-[#475569] mt-1">Free 30-min call · we'll review the numbers, system size and pricing options together.</p>
            </div>
          </div>

          {slots.length === 0 ? (
            <p className="text-[#475569] py-8 text-center">Loading times...</p>
          ) : (
            <SlotPicker slots={slots} chosen={chosenSlot} onChoose={setChosenSlot} />
          )}

          <div className="flex flex-wrap justify-between gap-3 mt-6">
            <button onClick={skipBooking} className="text-sm text-[#475569] hover:text-[#0A2540] underline" data-testid="slot-skip">
              Skip — call me to schedule
            </button>
            <button
              onClick={bookSlot}
              disabled={!chosenSlot || bookingSlot}
              className="btn-accent"
              data-testid="slot-confirm"
            >
              {bookingSlot ? "Booking..." : "Confirm appointment"}
            </button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div className="card-pb p-12 text-center" data-testid="step-5-success">
          <div className="w-16 h-16 bg-emerald-100 rounded-full grid place-items-center mx-auto mb-4">
            <ArrowRight className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="font-heading text-2xl text-[#0A2540] mb-2">All set!</h2>
          <p className="text-[#475569] mb-6">
            {chosenSlot
              ? `We've got you down for ${new Date(chosenSlot).toLocaleString([], { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}. We'll send a calendar reminder.`
              : "Our team will respond within 1 business day to schedule your consultation."}
          </p>
          <button
            onClick={() => { setStep(1); setType(null); setGoals([]); setNotes(""); setChosenSlot(null); setRequestId(null); }}
            className="btn-primary"
            data-testid="submit-another"
          >
            Submit another
          </button>
        </div>
      )}
    </div>
  );
}

function SlotPicker({ slots, chosen, onChoose }) {
  // Group slots by date
  const grouped = {};
  for (const s of slots) {
    const day = s.iso.slice(0, 10);
    grouped[day] = grouped[day] || [];
    grouped[day].push(s);
  }
  const days = Object.keys(grouped).slice(0, 7); // first 7 business days
  return (
    <div className="space-y-5" data-testid="slot-picker">
      {days.map((d) => {
        const date = new Date(d + "T12:00:00Z");
        const dayLabel = date.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
        return (
          <div key={d}>
            <div className="text-sm font-medium text-[#0A2540] mb-2">{dayLabel}</div>
            <div className="flex flex-wrap gap-2">
              {grouped[d].map((s) => {
                const time = new Date(s.iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
                const selected = chosen === s.iso;
                return (
                  <button
                    key={s.iso}
                    type="button"
                    disabled={!s.available}
                    onClick={() => onChoose(s.iso)}
                    data-testid={`slot-${s.iso}`}
                    className={`px-3 py-2 text-sm rounded-md border-2 transition-all ${
                      !s.available
                        ? "border-[#E2E8F0] bg-slate-100 text-slate-400 cursor-not-allowed line-through"
                        : selected
                        ? "border-[#C9A356] bg-[#C9A356] text-white font-medium"
                        : "border-[#E2E8F0] text-[#0A2540] hover:border-[#C9A356] hover:bg-[#FFFBF0]"
                    }`}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
